import styled from "styled-components/macro";
import {
  HELP_DESKTOP_MEDIA,
  HELP_OVERLAY_MEDIA,
  HELP_PANEL_WIDTH_PX,
  PROVIDER_HELP_OVERLAY_Z_INDEX,
  PROVIDER_HELP_PANEL_Z_INDEX,
} from "./Help.constants";

export const HelpFAQAnswerWrapper = styled.div`
  p {
    display: inline;
  }
`;

export const HelpPanelOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (${HELP_OVERLAY_MEDIA}) {
    display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
    position: fixed;
    inset: 63px 0 0 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: ${PROVIDER_HELP_OVERLAY_Z_INDEX};
  }
`;

export const HelpPanel = styled.aside<{ $isOpen: boolean }>`
  position: static;
  display: flex;
  flex-direction: column;
  width: ${HELP_PANEL_WIDTH_PX}px;
  min-width: ${HELP_PANEL_WIDTH_PX}px;
  height: 100%;
  max-height: 100vh;
  overflow: hidden;
  background: ${({ theme }) => theme.neutral50};
  transform: none;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  transition: opacity 240ms ease;
  z-index: 10;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (${HELP_DESKTOP_MEDIA}) {
    position: sticky;
    top: 0;
    height: 100vh;
    max-height: 100vh;
  }

  @media (${HELP_OVERLAY_MEDIA}) {
    position: fixed;
    top: 63px;
    right: 0;
    bottom: 0;
    width: min(100vw, ${HELP_PANEL_WIDTH_PX}px);
    min-width: 0;
    height: calc(100vh - 63px);
    border-left: 1px solid ${({ theme }) => theme.neutral200};
    transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "100%")});
    transition:
      transform 320ms cubic-bezier(0.2, 0, 0, 1),
      opacity 240ms ease;
    z-index: ${PROVIDER_HELP_PANEL_Z_INDEX};
  }
`;

export const HelpPanelHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  flex: 0 0 auto;
  gap: 8px;
  min-height: 64px;
  padding: 0 16px;
  background: ${({ theme }) => theme.neutral50};
  border-bottom: 1px solid ${({ theme }) => theme.neutral200};
`;

export const HelpPanelTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.greyDark};
  font-family: bold;
  font-size: 24px;
  line-height: 30px;
`;

export const HelpPanelCloseButton = styled.button`
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.greyDark};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.neutral100};
  }
`;

export const HelpPanelTabs = styled.div`
  display: flex;
  margin-bottom: 24px;
  padding: 2px;
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.neutral100};
  border-radius: 50px;
`;

export const HelpPanelTab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  min-height: 28px;
  border: 0;
  border-radius: 50px;
  padding: 5px 10px;
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.neutral100 : "transparent"};
  color: ${({ theme }) => theme.greyDark};
  cursor: pointer;
  font-family: ${({ $isActive }) => ($isActive ? "bold" : "light")};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  white-space: nowrap;
`;

export const HelpPanelBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 16px 16px;
  background: ${({ theme }) => theme.neutral50};
`;

export const HelpPanelVideoCard = styled.div`
  aspect-ratio: 16 / 9;
  margin-bottom: 24px;
  overflow: hidden;
  border-radius: 4px;
  background: ${({ theme }) => theme.neutral100};
`;

export const HelpPanelSectionLabel = styled.h3`
  margin: 20px 0 8px;
  color: ${({ theme }) => theme.greyDark};
  font-family: bold;
  font-size: 20px;
  line-height: 26px;
`;

export const HelpPanelAccordionCard = styled.div`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.neutral200};
  border-radius: 4px;

  .MuiAccordion-root {
    margin: 0 !important;
  }

  .MuiAccordion-root:before {
    width: 100%;
  }

  .MuiAccordionSummary-root {
    min-height: 48px;
    padding: 0 12px;
  }

  .MuiAccordionSummary-content p {
    color: ${({ theme }) => theme.greyDark};
    font-size: 16px;
    line-height: 22px;
  }

  .MuiAccordionSummary-content sup {
    font-size: 12px;
    line-height: 18px;
  }

  .MuiAccordionDetails-root {
    padding: 0 12px 20px !important;
  }
`;

export const HelpPanelResources = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 20px;
`;

export const HelpPanelResourceSection = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

export const HelpPanelResourceTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.greyDark};
  font-family: bold;
  font-size: 20px;
  line-height: 26px;
`;

export const HelpPanelResourceCard = styled.div`
  padding: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.neutral200};
  border-radius: 4px;
  background: ${({ theme }) => theme.white};
`;
